/**
 * Zod schemas and types for the `getSlFeedbackStats` callable function.
 *
 * Shared between the `@cip/functions` callable implementation and the
 * `@cip/webapp` client dashboard. The function is course-scoped and gated to
 * TA+ callers.
 *
 * Collection path for source data:
 *   `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slAiFeedback`
 *   `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slFeedbackEvaluation`
 */

import {z} from 'zod';

// ============================================================================
// Callable function name constant
// ============================================================================

/**
 * Export name of the Cloud Function and the string to pass to
 * `httpsCallable(functions, GET_SL_FEEDBACK_STATS_FUNCTION_NAME)`.
 *
 * Must match the export name in `src/apps/functions/src/index.ts`.
 */
export const GET_SL_FEEDBACK_STATS_FUNCTION_NAME = 'getslfeedbackstats';

// ============================================================================
// Request schema
// ============================================================================

/**
 * Schema for a request to `getSlFeedbackStats`.
 *
 * Only `courseId` is required for v1; future versions may add date-range or
 * per-section filters.
 */
export const GetSlFeedbackStatsRequestSchema = z.object({
  /** Course to aggregate stats for. Caller must be TA+ in this course. */
  courseId: z.string().min(1),
});

/** Request payload for the `getSlFeedbackStats` callable. */
export type GetSlFeedbackStatsRequest = z.infer<
  typeof GetSlFeedbackStatsRequestSchema
>;

// ============================================================================
// Response schemas
// ============================================================================

/**
 * Schema for a single day's feedback generation count in the 30-day timeseries.
 * Each entry represents one calendar day (UTC) bucketed by `generatedAt`.
 */
export const SlFeedbackDailyCountSchema = z.object({
  /**
   * ISO 8601 date string in UTC (e.g. `"2025-04-01"`). Represents the start of
   * the closed day interval `[date 00:00:00 UTC, date+1 00:00:00 UTC)`.
   */
  date: z.string(),
  /** Number of `slAiFeedback` docs with `generatedAt` falling on this date. */
  count: z.number().int().nonnegative(),
});

/** One day's generation count in the 30-day throughput timeseries. */
export type SlFeedbackDailyCount = z.infer<typeof SlFeedbackDailyCountSchema>;

/**
 * Schema for a model's share of AI feedback docs (top-5 breakdown).
 * The `"other"` entry aggregates all models outside the top 5 by volume.
 */
export const SlFeedbackModelCountSchema = z.object({
  /** Model name as stored in the `model` field, or `"other"` / `"(none)"`. */
  model: z.string(),
  /** Number of docs using this model. */
  count: z.number().int().nonnegative(),
});

/** One model's count in the top-5 model breakdown. */
export type SlFeedbackModelCount = z.infer<typeof SlFeedbackModelCountSchema>;

/**
 * Schema for the full v1 stats response from `getSlFeedbackStats`.
 *
 * All counts are derived from `collectionGroup` aggregation queries against the
 * course-scoped `slAiFeedback` and `slFeedbackEvaluation` collections.
 *
 * Brute eval-coverage caveat: `evalCoverageRatio` is
 * `count(slFeedbackEvaluation) / count(slAiFeedback where status == 'succeeded'
 * OR status == null)`. It is a ratio, not a per-user metric. If a single
 * section-week has multiple succeeded `slAiFeedback` docs (e.g. regenerations),
 * the denominator inflates and the ratio understates real coverage. This is an
 * accepted v1 tradeoff; a true join-based per-user metric is deferred.
 */
export const GetSlFeedbackStatsResponseSchema = z.object({
  // ── slAiFeedback totals ────────────────────────────────────────────────────

  /** Total `slAiFeedback` docs across all statuses for this course. */
  totalFeedbackDocs: z.number().int().nonnegative(),

  /**
   * Number of docs with `status === 'succeeded'` (or `status` absent, for
   * legacy docs without the field).
   */
  succeededCount: z.number().int().nonnegative(),

  /** Number of docs with `status === 'claiming'` (in-flight generation). */
  claimingCount: z.number().int().nonnegative(),

  /** Number of docs with `status === 'failed'`. */
  failedCount: z.number().int().nonnegative(),

  /**
   * Number of docs that are missing the `courseId` denorm field. Non-zero
   * values indicate the backfill has not yet completed. Use this counter to
   * verify backfill progress from the dashboard.
   */
  missingCourseIdCount: z.number().int().nonnegative(),

  /**
   * Top-5 models by doc count, plus an `"other"` bucket. The `"(none)"` entry
   * aggregates docs without a `model` field (succeeded legacy docs or claim
   * docs).
   *
   * Sorted descending by `count`.
   */
  modelBreakdown: z.array(SlFeedbackModelCountSchema),

  /**
   * Per-day generation counts for the last 30 days, ordered chronologically
   * (oldest to newest). Only days with at least one document are included.
   *
   * The window is the closed-open interval
   * `[now - 30 days 00:00:00 UTC, now)` bucketed by UTC calendar day.
   */
  throughputLast30Days: z.array(SlFeedbackDailyCountSchema),

  /**
   * ISO 8601 timestamp of the most recently generated `slAiFeedback` doc
   * (`generatedAt` field, descending). `null` if no succeeded docs exist.
   */
  lastGeneratedAt: z.string().nullable(),

  // ── slFeedbackEvaluation totals ────────────────────────────────────────────

  /** Total `slFeedbackEvaluation` docs for this course. */
  totalEvalDocs: z.number().int().nonnegative(),

  /** Number of evaluations with `helpful === 'up'`. */
  upCount: z.number().int().nonnegative(),

  /** Number of evaluations with `helpful === 'down'`. */
  downCount: z.number().int().nonnegative(),

  /**
   * Number of evaluations submitted while LLM feedback was enabled
   * (`llmFeedbackEnabled === true`).
   */
  evalWithLlmEnabledCount: z.number().int().nonnegative(),

  /**
   * Number of evaluations submitted while LLM feedback was disabled
   * (`llmFeedbackEnabled === false`).
   */
  evalWithLlmDisabledCount: z.number().int().nonnegative(),

  /**
   * Brute eval-coverage ratio: `totalEvalDocs / (succeededCount + legacySucceededCount)`.
   *
   * Returns `null` when the denominator is zero (no feedback docs).
   *
   * See schema-level caveat: this ratio understates real per-user coverage when
   * multiple succeeded docs exist for the same section-week (regenerations).
   */
  evalCoverageRatio: z.number().nullable(),

  // ── LLM-feedback opt-out funnel (active SLs only) ──────────────────────────

  /**
   * Funnel of active section leaders for this course:
   *
   * - `totalActiveSls` — distinct SLs with at least one section in the course
   *   where `section.isActive === true` and `section.isTestSection !== true`.
   *   Cover SLs are not counted unless they also primary-lead an active section.
   * - `viewedFeedbackSls` — subset of the above whose section has at least one
   *   `slAiFeedback` doc with `status === 'succeeded'` (or absent, treated as
   *   succeeded per legacy schema convention). This is a proxy for "feedback
   *   was generated and visible on their SL feedback page", not a literal
   *   page-view event.
   * - `optedOutSls` — subset of the above whose
   *   `users/{uid}/{courseId}/sectionSettings` doc has
   *   `noLlmSectionFeedback === true`. The opt-out blocks generation, so
   *   `optedOutSls` and `viewedFeedbackSls` should typically be disjoint.
   */
  optOuts: z.object({
    totalActiveSls: z.number().int().nonnegative(),
    viewedFeedbackSls: z.number().int().nonnegative(),
    optedOutSls: z.number().int().nonnegative(),
  }),

  // ── Student-reflection moderation ──────────────────────────────────────────

  /**
   * Aggregate stats over `studentReflectionModeration` verdicts for this course:
   *
   * - `totalVerdicts` — total verdicts written for the course (across statuses).
   * - `okCount` / `flaggedCount` / `erroredCount` — breakdown by verdict status.
   *   Sums to `totalVerdicts` only when there are no other (unknown) statuses.
   * - `topFlaggedCategories` — categories that triggered flagging, ordered by
   *   count descending. Derived from a doc fetch of flagged-only verdicts; may
   *   be capped to a top-N when the corpus grows.
   * - `missingCourseIdCount` — verdicts written before the denorm migration
   *   (no `courseId` field). Computed as `unfiltered total - filtered total`.
   *   Use to verify the backfill landed.
   */
  reflectionModeration: z.object({
    totalVerdicts: z.number().int().nonnegative(),
    okCount: z.number().int().nonnegative(),
    flaggedCount: z.number().int().nonnegative(),
    erroredCount: z.number().int().nonnegative(),
    topFlaggedCategories: z.array(
      z.object({
        category: z.string(),
        count: z.number().int().nonnegative(),
      }),
    ),
    missingCourseIdCount: z.number().int().nonnegative(),
  }),

  // ── Numeric-rating histograms (student reflections + SL debriefs) ─────────

  /**
   * Per-numeric-question histograms of student reflection and SL debrief
   * ratings, with both a cumulative (all weeks combined) and per-week breakdown
   * so the dashboard can toggle between the two via a dropdown.
   *
   * Each question entry carries a `collectionGroup` discriminator
   * (`"studentReflections"` for student emoji ratings or `"slDebriefs"` for
   * SL self-ratings) so the dashboard can label panels and route per-question
   * diagnostics to the correct source.
   *
   * `cumulative` and `perWeek` arrays are keyed by `value` (the rating bucket,
   * e.g. `1..5` for a 1-5 emoji scale). Rating values that didn't appear in
   * the data still appear in the array with `count: 0` so the chart axis
   * stays stable across loads.
   *
   * `missingCourseIdCount` is split per-collection — each value is computed
   * as `unfiltered total - filtered total` for that collection group, mirroring
   * the `slAiFeedback` diagnostic. Non-zero indicates the corresponding S11
   * backfill (`mise backfill-student-reflections-denorm` /
   * `mise backfill-sl-debriefs-denorm`) hasn't run.
   */
  numericRatingHistograms: z.object({
    questions: z.array(
      z.object({
        /** Question id from the survey config (e.g. `"emojiRating"`). */
        questionId: z.string(),
        /** Human-readable question text for chart labels. */
        questionText: z.string(),
        /**
         * Source collection-group for this question's data. Lets the
         * dashboard label panels (Student vs SL) and route diagnostics.
         */
        collectionGroup: z.enum(['studentReflections', 'slDebriefs']),
        /** Rating-scale bucket values (e.g. `[1, 2, 3, 4, 5]`). */
        scale: z.array(z.number()),
        /** Cumulative counts across all weeks, ordered to match `scale`. */
        cumulative: z.array(
          z.object({
            value: z.number(),
            count: z.number().int().nonnegative(),
          }),
        ),
        /** Per-week buckets ordered by ascending `sectionWeek`. */
        perWeek: z.array(
          z.object({
            sectionWeek: z.number().int().positive(),
            counts: z.array(
              z.object({
                value: z.number(),
                count: z.number().int().nonnegative(),
              }),
            ),
          }),
        ),
      }),
    ),
    /**
     * Diagnostic: docs missing the `courseId` denorm field, per source
     * collection. Non-zero values indicate the corresponding backfill has
     * not run.
     */
    missingCourseIdCount: z.object({
      studentReflections: z.number().int().nonnegative(),
      slDebriefs: z.number().int().nonnegative(),
    }),
  }),
});

/** Full v1 stats response from the `getSlFeedbackStats` callable. */
export type GetSlFeedbackStatsResponse = z.infer<
  typeof GetSlFeedbackStatsResponseSchema
>;
